import type {
  ChatShareError,
  ChatShareProvider,
  ChatShareResult,
} from "./types";

export type ParsedChatShareUrl = {
  provider: ChatShareProvider;
  shareId: string;
  /** Canonical HTTPS URL used for fetching. */
  url: URL;
};

function policyError(
  code: ChatShareError["code"],
  message: string,
): ChatShareError {
  return { code, message };
}

function normalizeHostname(hostname: string) {
  return hostname.replace(/\.$/, "").toLowerCase();
}

const chatgptHosts = new Set(["chatgpt.com", "www.chatgpt.com", "chat.openai.com"]);
const geminiHosts = new Set(["gemini.google.com", "www.gemini.google.com"]);
const geminiShortHosts = new Set(["g.co"]);
const geminiShareHosts = new Set(["share.gemini.google"]);

function parseShareIdSegments(
  segments: string[],
  allowEPrefix: boolean,
): string | null {
  if (segments.length === 0) {
    return null;
  }

  if (allowEPrefix && segments[0] === "e" && segments[1]) {
    const id = segments[1];
    return id.length > 0 && !id.includes("/") ? id : null;
  }

  const id = segments[0];
  if (!id || id.includes("/")) {
    return null;
  }

  // Reject empty, traversal-looking, or overly long IDs.
  if (id.length > 200 || id.includes("..") || id.includes("\\")) {
    return null;
  }

  return id;
}

/**
 * Validate and normalize a public ChatGPT or Gemini share URL for a given provider.
 * Gemini short links (`g.co/gemini/share/...`) are rewritten to `gemini.google.com`.
 */
export function parseAllowedChatShareUrl(
  rawUrl: string,
  provider: ChatShareProvider,
): ChatShareResult<ParsedChatShareUrl> {
  if (typeof rawUrl !== "string" || rawUrl.trim().length === 0) {
    return {
      ok: false,
      error: policyError("INVALID_URL", "A share URL is required."),
    };
  }

  let url: URL;

  try {
    url = new URL(rawUrl.trim());
  } catch {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Share URL is not a valid absolute URL.",
      ),
    };
  }

  if (url.protocol !== "https:") {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Only HTTPS share URLs are allowed.",
      ),
    };
  }

  if (url.username || url.password) {
    return {
      ok: false,
      error: policyError(
        "INVALID_URL",
        "Share URLs must not include credentials.",
      ),
    };
  }

  const hostname = normalizeHostname(url.hostname);
  const segments = url.pathname.split("/").filter(Boolean);

  if (provider === "chatgpt") {
    if (!chatgptHosts.has(hostname)) {
      return {
        ok: false,
        error: policyError(
          "UNSUPPORTED_HOST",
          "Use a public chatgpt.com or chat.openai.com share link.",
        ),
      };
    }

    if (segments[0] !== "share") {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "ChatGPT share URLs must look like https://chatgpt.com/share/...",
        ),
      };
    }

    const shareId = parseShareIdSegments(segments.slice(1), true);

    if (!shareId) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "ChatGPT share URL is missing a share id.",
        ),
      };
    }

    const path =
      segments[1] === "e"
        ? `/share/e/${encodeURIComponent(shareId)}`
        : `/share/${encodeURIComponent(shareId)}`;
    const canonical = new URL(`https://chatgpt.com${path}`);

    return {
      ok: true,
      value: {
        provider: "chatgpt",
        shareId,
        url: canonical,
      },
    };
  }

  if (provider === "gemini") {
    let shareId: string | null = null;
    let canonical: URL | null = null;

    if (geminiShortHosts.has(hostname)) {
      if (segments[0] !== "gemini" || segments[1] !== "share") {
        return {
          ok: false,
          error: policyError(
            "INVALID_URL",
            "Gemini short URLs must look like https://g.co/gemini/share/...",
          ),
        };
      }

      shareId = parseShareIdSegments(segments.slice(2), false);
      if (shareId) {
        canonical = new URL(
          `https://gemini.google.com/share/${encodeURIComponent(shareId)}`,
        );
      }
    } else if (geminiShareHosts.has(hostname)) {
      // Newer short links (share.gemini.google/{token}) resolve to
      // gemini.google.com/share/{id} via redirect — keep the short URL here and
      // resolve it during fetch.
      shareId = parseShareIdSegments(segments, false);
      if (shareId) {
        canonical = new URL(
          `https://share.gemini.google/${encodeURIComponent(shareId)}`,
        );
      }
    } else if (geminiHosts.has(hostname)) {
      if (segments[0] !== "share") {
        return {
          ok: false,
          error: policyError(
            "INVALID_URL",
            "Gemini share URLs must look like https://gemini.google.com/share/...",
          ),
        };
      }

      shareId = parseShareIdSegments(segments.slice(1), false);
      if (shareId) {
        canonical = new URL(
          `https://gemini.google.com/share/${encodeURIComponent(shareId)}`,
        );
      }
    } else {
      return {
        ok: false,
        error: policyError(
          "UNSUPPORTED_HOST",
          "Use a public gemini.google.com, share.gemini.google, or g.co/gemini/share link.",
        ),
      };
    }

    if (!shareId || !canonical) {
      return {
        ok: false,
        error: policyError(
          "INVALID_URL",
          "Gemini share URL is missing a share id.",
        ),
      };
    }

    return {
      ok: true,
      value: {
        provider: "gemini",
        shareId,
        url: canonical,
      },
    };
  }

  return {
    ok: false,
    error: policyError(
      "UNSUPPORTED_PROVIDER",
      "Unsupported chat share provider.",
    ),
  };
}

export function isChatShareProvider(value: unknown): value is ChatShareProvider {
  return value === "chatgpt" || value === "gemini";
}
